import {
  accountADService,
  accountDomainsService,
  accountEntitiesService,
  accountMigrationService,
  accountOsintService,
  accountsService,
  accountVariablesService,
} from '@/client/accounts';
import {
  accountAdminTasksService,
  adminTasksService,
} from '@/client/admin-tasks';
import { authService, impersonationService } from '@/client/auth';
import { branchesService } from '@/client/branches';
import {
  benchmarksService,
  campaignsService,
  campaignTemplatesService,
  falsePositiveIpsService,
  masterTemplatesService,
  phishingCharacteristicsService,
  phishingSignsService,
  reconAiService,
  suppressedDomainsService,
} from '@/client/campaigns';
import { masterTemplateReportsService } from '@/client/campaigns/master-template-reports.service';
import { coursesService } from '@/client/courses';
import { customRolesService } from '@/client/custom-roles';
import { emailTemplatesService } from '@/client/email-templates';
import filesService from '@/client/files/files.service';
import { groupsService } from '@/client/groups';
import { learnerService } from '@/client/learner-course';
import { myPhishingService } from '@/client/my-phishing';
import { pageTemplatesService } from '@/client/page-templates';
import { profileSettingsService } from '@/client/profile-settings';
import { rolesService } from '@/client/roles';
import { senderProfilesService } from '@/client/sender-profiles';
import {
  systemEmailSendingConfigService,
  systemEmailsTemplatesService,
  systemEmailsTypesConfigsService,
} from '@/client/system-emails';
import { currentUserService, usersService } from '@/client/users';
import { accountGoogleWorkspaceService } from './accounts/account-google-workspace.service';
import axiosClientFactory from './axios-client.factory';
import { beeFreeService } from './bee-free/bee-free.service';
import { dashboardService } from './dashboard';
import { featuresFlagsService } from './feature-flags';
import { hubspotService } from './hubspot';
import { longLivedTokenService } from './long-lived-token';
import { operationLogService } from './operation-log';
import { plannerService } from './planner/planner.service';
import { quizzesService } from './quizzes';
import { reportsService } from './reports';
import { resellersService } from './resellers';
import { securityCultureService } from './security-culture';
import { securityProfileService } from './security-profile';
import { tagsService } from './tags';
import { thumbnailsService } from './thumbnails';
import { translationsService } from './translations';
import { ApiClientConfigType } from './types';

export const apiClientFactory = (
  config: ApiClientConfigType,
): {
  auth: ReturnType<typeof authService>;
  impersonation: ReturnType<typeof impersonationService>;
  accounts: ReturnType<typeof accountsService>;
  accountEntities: ReturnType<typeof accountEntitiesService>;
  accountDomains: ReturnType<typeof accountDomainsService>;
  accountVariables: ReturnType<typeof accountVariablesService>;
  accountOsint: ReturnType<typeof accountOsintService>;
  accountAdminTasks: ReturnType<typeof accountAdminTasksService>;
  adminTasks: ReturnType<typeof adminTasksService>;
  resellers: ReturnType<typeof resellersService>;
  adSync: ReturnType<typeof accountADService>;
  googleWorkspace: ReturnType<typeof accountGoogleWorkspaceService>;
  branches: ReturnType<typeof branchesService>;
  groups: ReturnType<typeof groupsService>;
  users: ReturnType<typeof usersService>;
  roles: ReturnType<typeof rolesService>;
  customRoles: ReturnType<typeof customRolesService>;
  default: ReturnType<typeof currentUserService>;
  files: ReturnType<typeof filesService>;
  campaigns: ReturnType<typeof campaignsService>;
  campaignTemplates: ReturnType<typeof campaignTemplatesService>;
  masterTemplateReports: ReturnType<typeof masterTemplateReportsService>;
  reconAi: ReturnType<typeof reconAiService>;
  benchmarks: ReturnType<typeof benchmarksService>;
  masterTemplates: ReturnType<typeof masterTemplatesService>;
  phishingCharacteristics: ReturnType<typeof phishingCharacteristicsService>;
  phishingSigns: ReturnType<typeof phishingSignsService>;
  senderProfiles: ReturnType<typeof senderProfilesService>;
  emailTemplates: ReturnType<typeof emailTemplatesService>;
  pageTemplates: ReturnType<typeof pageTemplatesService>;
  reports: ReturnType<typeof reportsService>;
  dashboard: ReturnType<typeof dashboardService>;
  profileSettingsService: ReturnType<typeof profileSettingsService>;
  systemEmailsTemplatesService: ReturnType<typeof systemEmailsTemplatesService>;
  axiosRef: ReturnType<typeof axiosClientFactory>;
  systemEmailsTypesConfigsService: ReturnType<
    typeof systemEmailsTypesConfigsService
  >;
  systemEmailSendingConfigService: ReturnType<
    typeof systemEmailSendingConfigService
  >;
  accountMigrationService: ReturnType<typeof accountMigrationService>;
  learnerCourses: ReturnType<typeof learnerService>;
  myPhishing: ReturnType<typeof myPhishingService>;
  courses: ReturnType<typeof coursesService>;
  thumbnails: ReturnType<typeof thumbnailsService>;
  hubspot: ReturnType<typeof hubspotService>;
  planner: ReturnType<typeof plannerService>;
  securityCulture: ReturnType<typeof securityCultureService>;
  securityProfile: ReturnType<typeof securityProfileService>;
  featureFlags: ReturnType<typeof featuresFlagsService>;
  longLivedToken: ReturnType<typeof longLivedTokenService>;
  quizzes: ReturnType<typeof quizzesService>;
  beeFree: ReturnType<typeof beeFreeService>;
  translations: ReturnType<typeof translationsService>;
  falsePositiveIps: ReturnType<typeof falsePositiveIpsService>;
  suppressedDomains: ReturnType<typeof suppressedDomainsService>;
  tags: ReturnType<typeof tagsService>;
  operationLogs: ReturnType<typeof operationLogService>;
} => {
  const client = axiosClientFactory(config);

  return {
    auth: authService(client),
    impersonation: impersonationService(client),
    accounts: accountsService(client),
    accountEntities: accountEntitiesService(client),
    accountDomains: accountDomainsService(client),
    accountVariables: accountVariablesService(client),
    accountOsint: accountOsintService(client),
    accountAdminTasks: accountAdminTasksService(client),
    adminTasks: adminTasksService(client),
    resellers: resellersService(client),
    adSync: accountADService(client),
    googleWorkspace: accountGoogleWorkspaceService(client),
    branches: branchesService(client),
    groups: groupsService(client),
    users: usersService(client),
    roles: rolesService(client),
    customRoles: customRolesService(client),
    default: currentUserService(client),
    files: filesService(client),
    campaigns: campaignsService(client),
    campaignTemplates: campaignTemplatesService(client),
    masterTemplateReports: masterTemplateReportsService(client),
    reconAi: reconAiService(client),
    benchmarks: benchmarksService(client),
    masterTemplates: masterTemplatesService(client),
    phishingCharacteristics: phishingCharacteristicsService(client),
    phishingSigns: phishingSignsService(client),
    senderProfiles: senderProfilesService(client),
    emailTemplates: emailTemplatesService(client),
    pageTemplates: pageTemplatesService(client),
    reports: reportsService(client),
    dashboard: dashboardService(client),
    profileSettingsService: profileSettingsService(client),
    systemEmailsTemplatesService: systemEmailsTemplatesService(client),
    axiosRef: client,
    systemEmailsTypesConfigsService: systemEmailsTypesConfigsService(client),
    systemEmailSendingConfigService: systemEmailSendingConfigService(client),
    accountMigrationService: accountMigrationService(client),
    courses: coursesService(client),
    learnerCourses: learnerService(client),
    myPhishing: myPhishingService(client),
    thumbnails: thumbnailsService(client),
    hubspot: hubspotService(client),
    planner: plannerService(client),
    securityCulture: securityCultureService(client),
    securityProfile: securityProfileService(client),
    featureFlags: featuresFlagsService(client),
    longLivedToken: longLivedTokenService(client),
    quizzes: quizzesService(client),
    beeFree: beeFreeService(client),
    translations: translationsService(client),
    falsePositiveIps: falsePositiveIpsService(client),
    suppressedDomains: suppressedDomainsService(client),
    tags: tagsService(client),
    operationLogs: operationLogService(client),
  };
};
